import { Box, Modal, TextField, Typography, Autocomplete, Checkbox, Chip } from "@mui/material";
import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import { postAPICall } from "lib/callApi";
import { CHECK_IF_FUNNEL_NAME_IS_UNIQUE } from "lib/constants";
import * as Yup from "yup";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function SaveAsModal({
  style,
  open,
  handleClose,
  header,
  title,
  ownerId,
  accessList,
  accessListOptions,
  handleSaveAs,
}) {
  const isFunnelTitleUnique = async (newTitle) => {
    try {
      let isUnique = false;
      const response = await postAPICall(CHECK_IF_FUNNEL_NAME_IS_UNIQUE, true, {
        title: newTitle,
      });
      if (response?.data?.success) {
        isUnique = response.data.data.isUnique;
      }
      return isUnique;
    } catch (error) {
      return false;
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string()
      .max(30, "Title cannot be more than 30 characters")
      .required("Title is required")
      .test("unique-title", "Funnel with that title already exists", async (value) => {
        const isUnique = await isFunnelTitleUnique(value);
        if (!isUnique) {
          throw new Yup.ValidationError("Funnel with that title already exists", value, "title");
        }
        return true;
      }),
  });

  const handleSubmit = (data) => {
    handleSaveAs(data.title, data.accessList);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography variant="h4" sx={{ mb: "1rem" }}>
          {header}
        </Typography>
        <Formik
          initialValues={{ title, accessList }}
          enableReinitialize
          validateOnBlur
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, touched, errors }) => (
            <Form>
              <TextField
                label="Title"
                defaultValue={values.title || "New Funnel"}
                onChange={(event) => setFieldValue("title", event.target.value)}
                error={Boolean(touched.title && errors.title)}
                helperText={touched.title && errors.title}
                sx={{ width: "100%" }}
                inputProps={{ maxLength: 30 }}
              />
              <Autocomplete
                multiple
                options={accessListOptions}
                disableCloseOnSelect
                // limitTags={3}
                getOptionLabel={(option) => option.name}
                onChange={(_, value) =>
                  setFieldValue(
                    "accessList",
                    value.map((t) => t.id)
                  )
                }
                value={accessListOptions.filter((option) => values.accessList.includes(option.id))}
                sx={{
                  "span.MuiAutocomplete-tag": {
                    backgroundColor: "#fff",
                    color: "#000",
                  },
                  ".MuiChip-root": {
                    backgroundColor: "#333",
                  },
                  mt: "1rem",
                }}
                renderOption={(props, option, { selected }) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                getOptionDisabled={(option) => option.id === ownerId}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      disabled={option.id === ownerId}
                    />
                  ))
                }
                renderInput={(params) => <TextField {...params} label="Access List" />}
              />
              <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <MDButton onClick={handleClose} color="gray80" style={{ marginRight: "10px" }}>
                  Cancel
                </MDButton>
                <MDButton type="submit" color="primaryRed">
                  Create
                </MDButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
}

export default SaveAsModal;
